import React from 'react';
import { UtilsContext } from '../../Context/UtilsContext';
import {
    ChevronDown,
    Tag,
    BookOpen,
    Sparkles,
    PenTool,
    Briefcase,
    GraduationCap,
    Package,
    FileText,
    FolderOpen,
    Printer,
    Armchair,
    LayoutGrid,
    Gamepad2
} from 'lucide-react';
import Product from '../Product';
import ProductService from '../../Utils/API/products';

export default function MegaMenu() {
    const { windowWidth } = React.useContext(UtilsContext);
    const [activeCategory, setActiveCategory] = React.useState(null);
    const [showAllCategories, setShowAllCategories] = React.useState(false);
    const [products, setProducts] = React.useState([]);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const timeoutRef = React.useRef(null);

    React.useEffect(() => {
        async function loadProducts() {
            const response = await ProductService.getProductsForMenu();
            setProducts(response);
        }
        loadProducts();
    }, []);

    const categories = [
        { id: 'ofertas', icon: Tag, label: 'Ofertas', special: true, url: '/categories/ofertas' },
        { id: 'escritura-y-correccion', icon: PenTool, label: 'Escritura y Corrección', url: '/categories/escritura-y-correccion' },
        { id: 'productos-de-oficina', icon: Briefcase, label: 'Productos de Oficina', url: '/categories/productos-de-oficina' },
        { id: 'productos-escolares', icon: GraduationCap, label: 'Productos Escolares', url: '/categories/productos-escolares' },
        { id: 'etiquetas-y-embalajes', icon: Package, label: 'Etiquetas y Embalajes', url: '/categories/etiquetas-y-embalajes' },
        { id: 'papel', icon: FileText, label: 'Papel, Cuadernos y Sobres', url: '/categories/papel-cuadernos-y-sobres' },
        { id: 'archivo-y-clasificacion', icon: FolderOpen, label: 'Archivo y Clasificación', url: '/categories/archivo-y-clasificacion' },
        { id: 'consumibles-y-accesorios-informaticos', icon: Printer, label: 'Consumibles y Accesorios Informáticos', url: '/categories/consumibles-y-accesorios-informaticos' },
        { id: 'mobiliario-de-oficina', icon: Armchair, label: 'Mobiliario de Oficina', url: '/categories/mobiliario-de-oficina' },
        { id: 'organizacion', icon: LayoutGrid, label: 'Organización', url: '/categories/organizacion' },
        { id: 'juegos', icon: Gamepad2, label: 'Juegos', url: '/categories/juegos' }
    ];

    // Categorías destacadas que se muestran en el menú principal
    const featuredCategories = ['ofertas', 'escritura-y-correccion', 'productos-de-oficina'];

    const handleCategoryClick = (categoryId, e) => {
        e.preventDefault();
        if (activeCategory === categoryId) {
            // Si ya está activa, cerrar
            setIsAnimating(false);
            setTimeout(() => {
                setActiveCategory(null);
            }, 200);
        } else {
            // Abrir nueva categoría
            setShowAllCategories(false);
            setIsAnimating(true);
            setActiveCategory(categoryId);
        }
    };

    const handleMenuClick = (e) => {
        e.preventDefault();
        if (showAllCategories) {
            // Si ya está abierto, cerrar
            setIsAnimating(false);
            setTimeout(() => {
                setShowAllCategories(false);
            }, 200);
        } else {
            // Abrir menú completo
            setIsAnimating(true);
            setActiveCategory(null);
            setShowAllCategories(true);
        }
    };

    // Cerrar al hacer click fuera
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('nav') && !e.target.closest('[data-megamenu-panel]')) {
                setIsAnimating(false);
                setTimeout(() => {
                    setActiveCategory(null);
                    setShowAllCategories(false);
                }, 200);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    if (windowWidth <= 600) return null;

    return (
        <>
            <nav
                style={{
                    width: '100%',
                    backgroundColor: '#fff',
                    borderTop: '1px solid #e5e7eb',
                    borderBottom: '1px solid #e5e7eb',
                    position: 'relative',
                    zIndex: 50
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 4rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        height: '56px'
                    }}
                >
                    {/* Botón "Todas las categorías" */}
                    <div
                        onClick={handleMenuClick}
                        style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.625rem 1.25rem',
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                color: showAllCategories ? '#028322' : '#1f2937',
                                backgroundColor: showAllCategories ? '#f0fdf4' : '#f9fafb',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                border: showAllCategories ? '2px solid #028322' : '2px solid #e5e7eb',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h14M3 10h14M3 14h14" strokeLinecap="round"/>
                            </svg>
                            <span>Todas las categorías</span>
                            <ChevronDown
                                size={16}
                                strokeWidth={2.5}
                                style={{
                                    transform: showAllCategories ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                }}
                            />
                        </div>
                    </div>

                    {/* Separador */}
                    <div style={{ width: '1px', height: '32px', backgroundColor: '#e5e7eb', margin: '0 0.5rem' }}/>

                    {/* Categorías destacadas */}
                    <div style={{ display: 'flex', gap: '0.25rem', flex: 1 }}>
                        {categories.filter(cat => featuredCategories.includes(cat.id)).map((category) => {
                            const isActive = activeCategory === category.id;
                            const isSpecial = category.special;

                            return (
                                <div
                                    key={category.id}
                                    style={{ position: 'relative' }}
                                >
                                    <a
                                        href={category.url}
                                        onClick={(e) => {
                                            if (!isSpecial) {
                                                handleCategoryClick(category.id, e);
                                            }
                                            // Si es especial (ofertas), dejamos que el navegador siga el enlace normalmente
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            padding: '0.625rem 1rem',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            color: isSpecial ? '#fff' : (isActive ? '#028322' : '#4b5563'),
                                            backgroundColor: isSpecial ? '#028322' : (isActive ? '#f0fdf4' : 'transparent'),
                                            borderRadius: '0.5rem',
                                            textDecoration: 'none',
                                            transition: 'all 0.2s ease',
                                            whiteSpace: 'nowrap',
                                            border: isActive && !isSpecial ? '1px solid #028322' : '1px solid transparent',
                                            cursor: 'pointer'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSpecial) {
                                                e.currentTarget.style.backgroundColor = '#f9fafb';
                                            } else {
                                                e.currentTarget.style.backgroundColor = '#027020';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSpecial) {
                                                e.currentTarget.style.backgroundColor = isActive ? '#f0fdf4' : 'transparent';
                                            } else {
                                                e.currentTarget.style.backgroundColor = '#028322';
                                            }
                                        }}
                                    >
                                        {React.createElement(category.icon, {
                                            size: 16,
                                            strokeWidth: 2.5,
                                            color: isActive && !isSpecial ? '#028322' : undefined
                                        })}
                                        <span>{category.label}</span>
                                        {!isSpecial && (
                                            <ChevronDown
                                                size={14}
                                                strokeWidth={2.5}
                                                style={{
                                                    transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s ease'
                                                }}
                                            />
                                        )}
                                    </a>
                                </div>
                            );
                        })}
                    </div>

                    {/* Slogan y Catálogos */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginLeft: 'auto' }}>
                        <span
                            style={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#6b7280',
                                whiteSpace: 'nowrap',
                                display: windowWidth < 1100 ? 'none' : 'block'
                            }}
                        >
                            Envío gratis desde 100€
                        </span>
                        <a
                            href="/categories/catalogos"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.625rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#4b5563',
                                backgroundColor: 'transparent',
                                borderRadius: '0.5rem',
                                textDecoration: 'none',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap',
                                border: '1px solid transparent'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            <BookOpen size={16} strokeWidth={2.5} />
                            <span>Catálogos</span>
                        </a>
                    </div>
                </div>
            </nav>

            {/* Mega panel con TODAS las categorías */}
            {showAllCategories && (
                <div
                    data-megamenu-panel
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #e5e7eb',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                        zIndex: 40,
                        opacity: isAnimating ? 1 : 0,
                        transform: isAnimating ? 'translateY(0)' : 'translateY(-10px)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        pointerEvents: isAnimating ? 'auto' : 'none'
                    }}
                >
                    <div
                        style={{
                            maxWidth: '1400px',
                            margin: '0 auto',
                            padding: '2rem 4rem',
                        }}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '0.5rem'
                            }}
                        >
                            {categories.map((category) => {
                                const CategoryIcon = category.icon;
                                return (
                                <a
                                    key={category.id}
                                    href={category.url}
                                    onMouseEnter={(e) => {
                                        setActiveCategory(category.id);
                                        e.currentTarget.style.backgroundColor = '#f0fdf4';
                                        e.currentTarget.style.borderColor = '#028322';
                                        const iconBox = e.currentTarget.querySelector('.category-icon-box');
                                        if (iconBox) {
                                            iconBox.style.backgroundColor = '#028322';
                                            iconBox.style.transform = 'scale(1.05)';
                                            const greenIcon = iconBox.children[0];
                                            const whiteIcon = iconBox.children[1];
                                            if (greenIcon) greenIcon.style.opacity = '0';
                                            if (whiteIcon) whiteIcon.style.opacity = '1';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.borderColor = 'transparent';
                                        const iconBox = e.currentTarget.querySelector('.category-icon-box');
                                        if (iconBox) {
                                            iconBox.style.backgroundColor = category.special ? '#028322' : '#f9fafb';
                                            iconBox.style.transform = 'scale(1)';
                                            const greenIcon = iconBox.children[0];
                                            const whiteIcon = iconBox.children[1];
                                            if (greenIcon) greenIcon.style.opacity = category.special ? '0' : '1';
                                            if (whiteIcon) whiteIcon.style.opacity = category.special ? '1' : '0';
                                        }
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        padding: '1rem 1.25rem',
                                        backgroundColor: 'transparent',
                                        borderRadius: '0.5rem',
                                        textDecoration: 'none',
                                        transition: 'all 0.15s ease',
                                        border: '1px solid transparent'
                                    }}
                                >
                                    <div
                                        className="category-icon-box"
                                        style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '0.5rem',
                                            backgroundColor: category.special ? '#028322' : '#f9fafb',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            transition: 'background-color 0.2s ease, transform 0.2s ease',
                                            position: 'relative'
                                        }}
                                    >
                                        <CategoryIcon
                                            size={24}
                                            strokeWidth={2.5}
                                            color="#028322"
                                            style={{
                                                position: 'absolute',
                                                transition: 'opacity 0.2s ease',
                                                opacity: category.special ? 0 : 1
                                            }}
                                        />
                                        <CategoryIcon
                                            size={24}
                                            strokeWidth={2.5}
                                            color="#fff"
                                            style={{
                                                position: 'absolute',
                                                transition: 'opacity 0.2s ease',
                                                opacity: category.special ? 1 : 0,
                                                pointerEvents: 'none'
                                            }}
                                            className="icon-white"
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.125rem' }}>
                                            {category.label}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                            Ver productos
                                        </div>
                                    </div>
                                    <ChevronDown size={18} strokeWidth={2.5} color="#9ca3af" style={{ transform: 'rotate(-90deg)' }} />
                                </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Panel de productos para categorías destacadas */}
            {activeCategory && !showAllCategories && (
                <div
                    data-megamenu-panel
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #e5e7eb',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                        zIndex: 40,
                        opacity: isAnimating ? 1 : 0,
                        transform: isAnimating ? 'translateY(0)' : 'translateY(-10px)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        pointerEvents: isAnimating ? 'auto' : 'none'
                    }}
                >
                    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem', minHeight: '300px', maxHeight: '500px', overflowY: 'auto' }}>
                        <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '2px solid #028322' }}>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#028322', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {activeCategory === 'ofertas' && <Sparkles size={24} />}
                                {categories.find(c => c.id === activeCategory)?.label}
                            </h3>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                            {products[activeCategory]?.slice(0, 8).map((product) => (
                                <Product key={product._id} product={product} variant="megamenu" />
                            ))}
                            <div
                                onClick={() => { window.location.href = '/categories/' + activeCategory; }}
                                style={{
                                    width: '100%',
                                    maxWidth: '180px',
                                    backgroundColor: '#fff',
                                    borderRadius: '0.75rem',
                                    border: '2px dashed #028322',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '1.5rem 0.75rem',
                                    minHeight: '280px',
                                    gap: '0.75rem'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f0fdf4';
                                    e.currentTarget.style.borderColor = '#027220';
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(2, 131, 34, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.style.borderColor = '#028322';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    backgroundColor: '#f0fdf4',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#028322',
                                    transition: 'all 0.2s ease'
                                }}>
                                    <ChevronDown size={24} strokeWidth={2.5} style={{ transform: 'rotate(-90deg)' }} />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{
                                        fontWeight: 700,
                                        fontSize: '0.9375rem',
                                        marginBottom: '0.375rem',
                                        color: '#1f2937'
                                    }}>
                                        Ver todos
                                    </div>
                                    <div style={{
                                        fontSize: '0.8125rem',
                                        color: '#6b7280',
                                        lineHeight: '1.4'
                                    }}>
                                        Explora la categoría
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
